import React, { useState, useMemo } from 'react';
import PlaybookTimelineEntry from './PlaybookTimelineEntry';
import { usePlaybookTimeline } from '../hooks/usePlaybookTimeline';
import type { TransitionLogEntry } from '../types/playbook.types';

interface PlaybookTimelineProps {
  instanceId: string;
}

const PAGE_SIZE = 10;

const PlaybookTimeline: React.FC<PlaybookTimelineProps> = ({ instanceId }) => {
  const { logs, loading, error } = usePlaybookTimeline(instanceId);
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...logs].sort((a, b) => new Date(b.executed_at).getTime() - new Date(a.executed_at).getTime()),
    [logs],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error}
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <p className="py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
        No transitions recorded yet.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Timeline */}
      <div className="relative space-y-4">
        {/* Connector line */}
        <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-gray-200 dark:bg-gray-700" />

        {paginated.map((entry: TransitionLogEntry) => (
          <PlaybookTimelineEntry key={entry.id} entry={entry} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PlaybookTimeline;
